import { Link, NavLink, Outlet } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import {
  LayoutDashboard, Ticket, Plug, Users, CreditCard, KeyRound, BookOpen, Shield, LogOut, Server, Boxes
} from "lucide-react";
import { useT } from "@/lib/i18n";
import LangSwitcher from "@/components/LangSwitcher";
import RatchetLogo from "@/components/RatchetLogo";

export default function AppLayout() {
  const { user, org, logout } = useAuth();
  const t = useT();

  if (!org) return null;

  const NAV = [
    { to: "/app", label: t("side.dashboard"), Icon: LayoutDashboard, end: true, key: "nav-dashboard" },
    { to: "/app/cards", label: t("side.cards"), Icon: Ticket, key: "nav-cards" },
    { to: "/app/knowledge", label: t("side.knowledge"), Icon: BookOpen, key: "nav-knowledge" },
    { to: "/app/integrations", label: t("side.integrations"), Icon: Plug, key: "nav-integrations" },
    { to: "/app/bridge", label: "Bridge", Icon: Server, key: "nav-bridge" },
    { to: "/app/squads", label: "Squads", Icon: Boxes, key: "nav-squads" },
    { to: "/app/team", label: t("side.team"), Icon: Users, key: "nav-team" },
    { to: "/app/api-keys", label: t("side.apikeys"), Icon: KeyRound, key: "nav-api-keys" },
    { to: "/app/billing", label: t("side.billing"), Icon: CreditCard, key: "nav-billing" },
  ];

  return (
    <div className="min-h-screen bg-background flex">
      <aside className="w-60 border-r border-white/5 flex flex-col bg-[#070707] sticky top-0 h-screen">
        <Link to="/" className="px-5 h-16 flex items-center gap-2 border-b border-white/5" data-testid="sidebar-brand">
          <RatchetLogo size={28} />
          <span className="font-display font-black tracking-tight">RATCHET</span>
        </Link>
        <div className="px-5 py-4 border-b border-white/5">
          <div className="overline">{t("side.workspace")}</div>
          <div className="text-sm font-medium mt-1 truncate" data-testid="sidebar-org-name">{org.name}</div>
          <div className="mt-1 inline-flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-wider">
            <span className="status-dot bg-primary animate-pulse-glow" />
            <span className="text-primary">{org.plan}</span>
          </div>
        </div>
        <nav className="flex-1 p-3 space-y-0.5 overflow-y-auto">
          {NAV.map((n) => (
            <NavLink
              key={n.to} to={n.to} end={n.end}
              data-testid={n.key}
              className={({ isActive }) =>
                `flex items-center gap-2.5 px-3 py-2 text-sm transition ${
                  isActive ? "bg-white/5 text-white border-l-2 border-primary pl-[10px]"
                           : "text-white/60 hover:text-white hover:bg-white/5"
                }`
              }
            >
              <n.Icon className="w-4 h-4" />
              {n.label}
            </NavLink>
          ))}
          {user?.is_super_admin && (
            <NavLink to="/app/admin"
              data-testid="nav-admin"
              className={({ isActive }) =>
                `mt-3 flex items-center gap-2.5 px-3 py-2 text-sm transition border-t border-white/5 pt-4 ${
                  isActive ? "text-primary" : "text-white/60 hover:text-primary"
                }`
              }>
              <Shield className="w-4 h-4" /> Super Admin
            </NavLink>
          )}
        </nav>
        <div className="border-t border-white/5 p-3 space-y-2">
          <div className="px-3 py-2">
            <div className="text-sm truncate" data-testid="sidebar-user-name">{user?.name}</div>
            <div className="font-mono text-[10px] text-white/40 truncate">{user?.email}</div>
          </div>
          <div className="px-3"><LangSwitcher /></div>
          <button onClick={logout} className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-white/60 hover:text-white hover:bg-white/5"
            data-testid="sidebar-logout-btn">
            <LogOut className="w-4 h-4" /> {t("side.signout")}
          </button>
        </div>
      </aside>
      <main className="flex-1 min-h-screen">
        <Outlet />
      </main>
    </div>
  );
}
